import React, { createContext, useContext } from "react";
import {
  createInstance,
  OptimizelyProvider,
  setLogger,
  useDecision,
} from "@optimizely/react-sdk";
import Cookies from "js-cookie";
import { GtmService } from "../Components/Booking/GtmService";

type Feature = {
  name: string;
  key: string;
  isBookingFeature: boolean;
};

const featureConfig: readonly Feature[] = [
  {
    name: "Enable new location dropdown",
    key: "enable_new_location_dropdown",
    isBookingFeature: true,
  },
  {
    name: "Enable new booking flow",
    key: "enable_new_booking_flow",
    isBookingFeature: false,
  },
  {
    name: "Enable price in hero",
    key: "enable_price_in_hero",
    isBookingFeature: false,
  },
] as const;

type FeatureFlagsContextType = {
  isFeatureEnabled: (key: (typeof featureConfig)[number]["key"]) => boolean;
  clientReady: boolean;
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  isFeatureEnabled: () => false,
  clientReady: false,
});

type FeatureFlagsProviderProps = {
  sdkKey: string;
  children: React.ReactNode;
};

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  sdkKey,
  children,
}) => {
  setLogger(null);

  const optimizelyClient = createInstance({ sdkKey });
  const [clientReady, setClientReady] = React.useState(false);
  const [trackedFeatures, setTrackedFeatures] = React.useState<string[]>([]);

  const isFeatureEnabled = (
    key: (typeof featureConfig)[number]["key"]
  ): boolean => {
    const [decision, clientReady] = useDecision(key);
    if (!clientReady) {
      return false;
    }
    
    if (!trackedFeatures.includes(key)) {
      const feature = featureConfig.find((feature) => feature.key === key);

      if (feature && feature.isBookingFeature) {
        GtmService.trackExperiment(feature.name, decision.enabled);
        setTrackedFeatures([...trackedFeatures, key]);
      }
    }

    setClientReady(clientReady);
    return decision.enabled;
  };

  const value = {
    isFeatureEnabled,
    clientReady,
  };

  return (
    <OptimizelyProvider
      optimizely={optimizelyClient}
      timeout={500}
      user={{ id: Cookies.get("EPiServer_CMS_AnonymousId") ?? null }}
    >
      <FeatureFlagsContext.Provider value={value}>
        {children}
      </FeatureFlagsContext.Provider>
    </OptimizelyProvider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
